import React, { useState } from 'react';
import { Calendar, FileText, TrendingUp, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import NewsletterModal from '../components/NewsletterModal';
import { newsletters } from '../data/blogPosts';

export default function Blog() {
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Sort newest first
  const sorted = [...newsletters].sort(
    (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );

  return (
    <>
      <SEO
        title="Insights — Newsletters & Updates | The Fuel Central"
        description="Weekly newsletters from The Fuel Central team covering platform updates, market intelligence, and fuel retail industry insights for gas station owners, operators, and investors."
        keywords="fuel retail insights, gas station newsletter, platform updates, market intelligence, California gas station market, petroleum industry analysis"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center mb-4">
                <TrendingUp className="w-12 h-12 mr-3" />
                <h1 className="text-4xl md:text-5xl font-bold">Insights</h1>
              </div>
              <p className="text-xl text-blue-100">
                Weekly newsletters from The Fuel Central team
              </p>
            </div>
          </div>
        </div>

        {/* Newsletter Grid */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sorted.map((item) => (
                <Link
                  key={item.id}
                  to={`/blog/${item.slug}`}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden flex flex-col"
                >
                  {/* Card header accent */}
                  <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-700 w-full" />

                  <div className="p-6 flex flex-col flex-1">
                    {/* Date & PDF badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(item.publishedDate)}
                      </span>
                      <span className="inline-flex items-center text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded">
                        <FileText className="w-3 h-3 mr-1" />
                        Article
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                      {item.title}
                    </h2>

                    {/* Subtitle */}
                    {item.subtitle && (
                      <p className="text-sm text-blue-600 font-medium mb-2">{item.subtitle}</p>
                    )}

                    {/* Excerpt */}
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
                      {item.excerpt}
                    </p>

                    {/* Read link */}
                    <div className="flex items-center text-blue-600 font-medium text-sm group-hover:text-blue-700 transition-colors">
                      <span>Read Article</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {sorted.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No newsletters available yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-blue-600 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Stay in the Loop</h2>
              <p className="text-blue-100 mb-6">
                Get weekly updates on new features, data expansions, and market intelligence
                delivered straight to your inbox.
              </p>
              <button
                onClick={() => setShowNewsletterModal(true)}
                className="inline-block bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                Subscribe to Updates
              </button>
            </div>
          </div>
        </div>
      </div>

      <NewsletterModal
        isOpen={showNewsletterModal}
        onClose={() => setShowNewsletterModal(false)}
      />
    </>
  );
}
